import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import {
  ENV_CONFIG,
  EnvironmentConfig,
  LoggerService,
  throwIfAlreadyLoaded
} from '@nibras/core/common';
import { Driver, NgForageConfig, NgForageOptions } from 'ngforage';
import { AppCacheService } from './app-cache.service';
import { CacheService } from './cache.service';
import { CommonService } from './common.service';
import { ConnectionResolver } from './connection.resolver';
import { CoreService } from './core.service';
import { DatasourceService } from './datasource.service';
import { DataStoreService } from './datastore.service';
import { DomService } from './dom.service';
import { ElementTemplateService } from './element-template.service';
import { EventsService } from './events.service';
import { ExceptionService } from './exception.service';
import { FlaggedEnum } from './flagged-enum';
import { HttpApplicationInterceptor } from './http.interceptor';
import { HttpService } from './http.service';
import { NibrasHooksService } from './nibras-hooks.service';
import { NibrasTitleService } from './nibras-title.service';
import { NonComplianceService } from './non-compliance.service';
import { RemarkConversionService } from './remark-conversion.service';
import { SecurityService } from './security.service';
import { SpeedTestService } from './speed-test.service';
import { CoreStoreEffects } from './store/core-store.effects';
import { coreStoreFeature } from './store/core-store.reducer';
import { TagService } from './tag.service';
import { UserProfileService } from './user-profile.service';
import { WorkFlowCommonService } from './workflow-common.service';

@NgModule({
  imports: [
    CommonModule,
    EffectsModule.forFeature([CoreStoreEffects]),
    HttpClientModule,
    RouterModule,
    StoreModule.forFeature(coreStoreFeature)
  ],
  exports: [CommonModule],
  providers: [
    AppCacheService,
    CoreService,
    CommonService,
    ConnectionResolver,
    DatasourceService,
    NonComplianceService,
    WorkFlowCommonService,
    CacheService,
    DataStoreService,
    DomService,
    ElementTemplateService,
    EventsService,
    ExceptionService,
    HttpService,
    LoggerService,
    NibrasHooksService,
    NibrasTitleService,
    RemarkConversionService,
    SecurityService,
    SpeedTestService,
    TagService,
    UserProfileService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpApplicationInterceptor,
      multi: true
    },
    FlaggedEnum
  ]
})
export class CoreModule {
  constructor(
    @Optional()
    @SkipSelf()
    parentModule: CoreModule,
    conf: NgForageConfig
  ) {
    throwIfAlreadyLoaded(parentModule, CoreModule.name);

    this.configureNgForage(conf);
  }

  static forRoot(config: EnvironmentConfig): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [{ provide: ENV_CONFIG, useValue: config }]
    };
  }

  private configureNgForage(conf: NgForageConfig) {
    // Set the database name
    conf.name = 'Nibras_Client_DataStore_DB';

    // Set the store name (e.g. in IndexedDB this is the dataStore)
    conf.storeName = 'ElementStore';

    // Set default cache time to 5 minutes
    conf.cacheTime = 300000;

    // Set driver to local storage
    //conf.driver = NgForageConfig.DRIVER_INDEXEDDB;

    // Set the driver to indexed db if available,
    // falling back to websql
    // falling back to local storage
    conf.driver = [Driver.INDEXED_DB, Driver.WEB_SQL, Driver.LOCAL_STORAGE];

    // Set websql database size
    conf.size = 1024 * 1024 * 4;

    // Set DB version. Currently unused.
    conf.version = 1.0;

    // Configure in elementsStore
    const elementStore: NgForageOptions = {
      version: 1.0,
      name: 'Nibras_Client_DataStore_DB',
      storeName: 'ElementStore'
    };
    conf.configure(elementStore);

    // Configure in attributesStore
    const attributeStore: NgForageOptions = {
      version: 1.0,
      name: 'Nibras_Client_DataStore_DB',
      storeName: 'AttributeStore'
    };
    conf.configure(attributeStore);

    // Configure in attributesStore
    const attributeValueStore: NgForageOptions = {
      version: 1.0,
      name: 'Nibras_Client_DataStore_DB',
      storeName: 'AttributeValueStore'
    };
    conf.configure(attributeValueStore);
  }
}
